import { Table as ReactTable } from "@tanstack/react-table";
import { twMerge } from "tailwind-merge";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { TableFooter } from "./TableFooter";
import { TableCell } from "./TableCell";
import { TableRow } from "./TableRow";

export type Props = {
  /** React Table instance */
  table: ReactTable<any>;
  /** Number of columns to span */
  colSpan: number;
};

/**
 * A table pagination component for use with @tanstack/react-table.
 */
export function TablePagination({ table, colSpan }: Props) {
  if (table.getPageCount() <= 1) return null;

  return (
    <TableFooter>
      <TableRow className="border-none">
        <TableCell colSpan={colSpan}>
          <div
            className="flex flex-row gap-2 justify-between w-full"
            data-testid="table-pagination"
          >
            <Button
              variant="secondary"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              iconComponent={ChevronLeft}
              iconPosition="left"
              data-testid="table-pagination-prev"
            >
              Previous
            </Button>

            <div className="flex-row gap-2 hidden md:flex">
              {[...Array(table.getPageCount()).keys()].map((i) => {
                const page = i + 1;
                const isActive =
                  table.getState().pagination.pageIndex + 1 === page;
                const shouldShow =
                  page <= 3 ||
                  page > table.getPageCount() - 3 ||
                  (page >= table.getState().pagination.pageIndex &&
                    page <= table.getState().pagination.pageIndex + 2);

                return shouldShow ? (
                  <Button
                    key={page}
                    variant="ghost"
                    className={twMerge(
                      "w-10 h-10 text-brand-700",
                      isActive && "bg-brand-100 text-brand-800"
                    )}
                    onClick={() => table.setPageIndex(i)}
                    data-testid={`table-pagination-page-${page}`}
                  >
                    {page}
                  </Button>
                ) : null;
              })}
            </div>

            <Button
              variant="secondary"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              iconComponent={ChevronRight}
              iconPosition="right"
              data-testid="table-pagination-next"
            >
              Next
            </Button>
          </div>
        </TableCell>
      </TableRow>
    </TableFooter>
  );
}

export default TablePagination;

